import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, LayoutDashboard, Users } from 'lucide-react';
import type { CampaignRecord } from '@workspace/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/contexts/confirmContext';
import {
  CAMPAIGN_BOARD_LABEL,
  campaignBoardStatus,
  campaignStatusButton,
  canEditAudience,
  canStopCampaign,
} from '../api/campaignActions';
import { campaignClonePath, campaignDetailPath } from '../api/campaignPaths';
import { campaignEndsAtDateLabel } from '../api/campaignDisplay';
import { activateUnreachableMessage } from '../api/campaignWizardRules';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import { ContactAgentAudienceDialog } from './ContactAgentAudienceDialog';

type CampaignListProps = {
  campaigns: CampaignRecord[];
  onUpdated: (campaign: CampaignRecord) => void;
  onDeleted: (campaignId: string) => void;
  onError: (message: string) => void;
};

export function ContactAgentCampaignList({
  campaigns,
  onUpdated,
  onDeleted,
  onError,
}: CampaignListProps) {
  const mutations = useCampaignMutations(onUpdated, onDeleted, onError);

  return (
    <>
      {campaigns.map(campaign => (
        <CampaignRow
          key={campaign.id}
          campaign={campaign}
          onStatus={mutations.changeStatus}
          onStop={() => void mutations.changeStatus(campaign, 'COMPLETED')}
          onAudience={() => mutations.setAudienceCampaign(campaign)}
          onDelete={() => void mutations.deleteCampaign(campaign)}
        />
      ))}
      <ContactAgentAudienceDialog
        campaign={mutations.audienceCampaign}
        open={mutations.audienceCampaign !== null}
        onOpenChange={open => {
          if (!open) {
            mutations.setAudienceCampaign(null);
          }
        }}
        onSaved={updated => {
          onUpdated(updated);
          mutations.setAudienceCampaign(null);
        }}
      />
    </>
  );
}

export function CampaignActionBar({
  campaign,
  onUpdated,
  onDeleted,
  onError,
}: {
  campaign: CampaignRecord;
  onUpdated: (campaign: CampaignRecord) => void;
  onDeleted: (campaignId: string) => void;
  onError: (message: string) => void;
}) {
  const mutations = useCampaignMutations(onUpdated, onDeleted, onError);
  const statusAction = campaignStatusButton(campaign.status);
  const audienceEnabled = canEditAudience(campaign.status);

  return (
    <>
      <div className='flex flex-wrap items-center gap-2'>
        {statusAction ? (
          <Button
            size='sm'
            variant={campaign.status === 'DRAFT' ? 'default' : 'outline'}
            onClick={() => void mutations.changeStatus(campaign, statusAction.next)}
          >
            {statusAction.label}
          </Button>
        ) : null}
        {canStopCampaign(campaign.status) ? (
          <Button size='sm' variant='outline' onClick={() => void mutations.changeStatus(campaign, 'COMPLETED')}>
            Detener
          </Button>
        ) : null}
        <Button
          size='sm'
          variant='outline'
          disabled={!audienceEnabled}
          title={audienceEnabled ? 'Editar publico objetivo' : 'Solo se edita el publico en borrador'}
          onClick={() => mutations.setAudienceCampaign(campaign)}
        >
          <Users className='mr-1 h-4 w-4' />
          Publico
        </Button>
        <Button size='sm' variant='outline' asChild>
          <Link to={campaignClonePath(campaign.id)}>
            <Copy className='mr-1 h-4 w-4' />
            Clonar
          </Link>
        </Button>
        <Button size='sm' variant='outline' onClick={() => void mutations.deleteCampaign(campaign)}>
          Eliminar
        </Button>
      </div>
      <ContactAgentAudienceDialog
        campaign={mutations.audienceCampaign}
        open={mutations.audienceCampaign !== null}
        onOpenChange={open => {
          if (!open) {
            mutations.setAudienceCampaign(null);
          }
        }}
        onSaved={updated => {
          onUpdated(updated);
          mutations.setAudienceCampaign(null);
        }}
      />
    </>
  );
}

function useCampaignMutations(
  onUpdated: (campaign: CampaignRecord) => void,
  onDeleted: (campaignId: string) => void,
  onError: (message: string) => void,
) {
  const { confirm } = useConfirm();
  const [audienceCampaign, setAudienceCampaign] = useState<CampaignRecord | null>(null);

  const changeStatus = async (campaign: CampaignRecord, next: CampaignRecord['status']) => {
    if (next === 'ACTIVE' && (campaign.status === 'DRAFT' || campaign.status === 'PAUSED')) {
      await startCampaign(confirm, campaign, onUpdated, onError);
      return;
    }
    const accepted = await confirmStatusChange(confirm, campaign, next);
    if (!accepted) {
      return;
    }
    try {
      const updated = await mutateCampaignStatus(campaign, next);
      onUpdated(updated);
    } catch (err) {
      onError(contactAgentError(err));
    }
  };

  const deleteCampaign = async (campaign: CampaignRecord) => {
    const accepted = await confirm({
      title: 'Eliminar campaña',
      message: `Se elimina "${campaign.name.slice(0, 80)}". Los chats quedan en el historial. No se vuelve a contactar a nadie desde esta campaña.`,
      confirmText: 'Eliminar',
      variant: 'danger',
    });
    if (!accepted) {
      return;
    }
    try {
      await contactAgentApi.deleteCampaign(campaign.id);
      onDeleted(campaign.id);
    } catch (err) {
      onError(contactAgentError(err));
    }
  };

  return { audienceCampaign, setAudienceCampaign, changeStatus, deleteCampaign };
}

async function startCampaign(
  confirm: (options: {
    title?: string;
    message: string;
    confirmText?: string;
    variant?: 'danger' | 'primary';
  }) => Promise<boolean>,
  campaign: CampaignRecord,
  onUpdated: (campaign: CampaignRecord) => void,
  onError: (message: string) => void,
): Promise<void> {
  try {
    const preview = await contactAgentApi.activationPreview(campaign.id);
    const unreachableNote =
      preview.unreachableCount > 0
        ? ` ${activateUnreachableMessage({
            eligibleCount: preview.eligibleCount,
            unreachableCount: preview.unreachableCount,
            channelKind: campaign.channelKind,
          })}`
        : '';
    const accepted = await confirm({
      title: campaign.status === 'PAUSED' ? 'Reanudar campaña' : 'Iniciar campaña',
      message:
        campaign.status === 'PAUSED'
          ? `Se reanuda la misma audiencia congelada. No se agregan contactos nuevos.${unreachableNote}`
          : preview.unreachableCount > 0
            ? activateUnreachableMessage({
                eligibleCount: preview.eligibleCount,
                unreachableCount: preview.unreachableCount,
                channelKind: campaign.channelKind,
              })
            : `Vas a avisar a ${preview.eligibleCount}. ¿Seguimos?`,
      confirmText: campaign.status === 'PAUSED' ? 'Reanudar' : 'Iniciar campaña',
    });
    if (!accepted) {
      return;
    }
    const updated = await contactAgentApi.activateCampaign(campaign.id, {
      previewId: preview.previewId,
      audienceRevision: preview.audienceRevision,
      audienceHash: preview.audienceHash,
    });
    onUpdated(updated);
  } catch (err) {
    onError(contactAgentError(err));
  }
}

async function mutateCampaignStatus(
  campaign: CampaignRecord,
  next: CampaignRecord['status'],
): Promise<CampaignRecord> {
  if (next === 'PAUSED') {
    return contactAgentApi.pauseCampaign(campaign.id);
  }
  if (next === 'COMPLETED') {
    return contactAgentApi.completeCampaign(campaign.id);
  }
  return contactAgentApi.updateCampaignStatus(campaign.id, next);
}

async function confirmStatusChange(
  confirm: (options: {
    title?: string;
    message: string;
    confirmText?: string;
    variant?: 'danger' | 'primary';
  }) => Promise<boolean>,
  campaign: CampaignRecord,
  next: CampaignRecord['status'],
): Promise<boolean> {
  if (campaign.status === 'DRAFT' && next === 'ACTIVE') {
    return confirm({
      title: 'Iniciar campaña',
      message:
        'Vas a contactar solo a la audiencia ya revisada. No se agregan destinatarios despues de iniciar. El opt-out sigue bloqueando.',
      confirmText: 'Iniciar campaña',
    });
  }
  if (campaign.status === 'PAUSED' && next === 'ACTIVE') {
    return confirm({
      title: 'Reanudar campaña',
      message: 'Se reanuda la misma audiencia congelada. No se agregan contactos nuevos.',
      confirmText: 'Reanudar',
    });
  }
  if (next === 'COMPLETED') {
    return confirm({
      title: 'Detener campaña',
      message: 'Deja de contactar. Esta campaña no se puede reactivar; podes clonarla como borrador.',
      confirmText: 'Detener',
    });
  }
  return true;
}

function CampaignRow({
  campaign,
  onStatus,
  onStop,
  onAudience,
  onDelete,
}: {
  campaign: CampaignRecord;
  onStatus: (campaign: CampaignRecord, next: CampaignRecord['status']) => Promise<void> | void;
  onStop: () => void;
  onAudience: () => void;
  onDelete: () => void;
}) {
  const statusAction = campaignStatusButton(campaign.status);
  const audienceEnabled = canEditAudience(campaign.status);
  const board = campaignBoardStatus(campaign);
  const started = campaign.conversationsStarted ?? 0;
  const responded = campaign.conversationsResponded ?? 0;

  return (
    <div className='flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-start sm:justify-between'>
      <div>
        <Link
          to={campaignDetailPath(campaign.id)}
          className='font-medium hover:underline'
        >
          {campaign.name}
        </Link>
        <p className='text-sm text-muted-foreground'>
          {campaign.purpose === 'AVISO' ? 'Aviso / contacto' : 'Cobranzas'} · {campaign.agent?.name ?? 'Agente'} ·{' '}
          {campaignEndsAtDateLabel(campaign.endsAt)}
        </p>
        <p className='mt-1 text-sm text-muted-foreground'>
          {started} iniciadas · {responded} respondidas
        </p>
      </div>
      <div className='flex flex-wrap items-center gap-2'>
        <Badge variant={boardVariant(board)}>{CAMPAIGN_BOARD_LABEL[board]}</Badge>
        <Button size='sm' variant='outline' asChild>
          <Link to={campaignDetailPath(campaign.id)}>
            <LayoutDashboard className='mr-1 h-4 w-4' />
            Tablero
          </Link>
        </Button>
        {statusAction ? (
          <Button size='sm' variant='outline' onClick={() => void onStatus(campaign, statusAction.next)}>
            {statusAction.label}
          </Button>
        ) : null}
        {canStopCampaign(campaign.status) ? (
          <Button size='sm' variant='outline' onClick={onStop}>
            Detener
          </Button>
        ) : null}
        <Button
          size='sm'
          variant='outline'
          disabled={!audienceEnabled}
          title={audienceEnabled ? 'Editar publico objetivo' : 'Solo se edita el publico en borrador'}
          onClick={onAudience}
        >
          <Users className='mr-1 h-4 w-4' />
          Publico
        </Button>
        <Button size='sm' variant='outline' asChild>
          <Link to={campaignClonePath(campaign.id)}>
            <Copy className='mr-1 h-4 w-4' />
            Clonar
          </Link>
        </Button>
        <Button size='sm' variant='outline' onClick={onDelete}>
          Eliminar
        </Button>
      </div>
    </div>
  );
}

function boardVariant(status: ReturnType<typeof campaignBoardStatus>): 'default' | 'secondary' | 'outline' {
  if (status === 'STARTED' || status === 'RESUMED') {
    return 'default';
  }
  if (status === 'PAUSED') {
    return 'secondary';
  }
  return 'outline';
}
